'use client';

import React, { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Stack,
  Button,
  Popover,
  Tooltip,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';

import { POST_MEMO_MAX_LENGTH } from 'src/utils/post-memo/post-memo-key';
import { POST_MEMO_UPGRADE_DESCRIPTION } from 'src/utils/post-memo/post-memo-plan-gate';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import PlanUpgradePopover from 'src/components/cmui/plan-upgrade/PlanUpgradePopover';
import { usePlanUpgradePopover } from 'src/components/cmui/plan-upgrade/use-plan-upgrade-popover';

import { useDemoActionGuard } from 'src/sections/common/demo/use-demo-guard';
import { usePostMemoOptional } from 'src/sections/common/post-memo/use-post-memo-hook';

const DEMO_MEMO_MSG = '데모에서는 메모를 저장할 수 없어요';
/** 체험 플랜의 아이콘 툴팁 — 다른 플랜 게이트와 같은 문구 */
const PLAN_BLOCKED_TOOLTIP = '플랜 업그레이드가 필요해요';

/** 셀에 보이는 본문의 폭 — 기본 세 줄까지 보이고 넘치면 줄임표, 전체는 툴팁·팝오버 */
const MEMO_CELL_WIDTH = 240;
const MEMO_CELL_LINES = 3;

/**
 * 메모 본문 → 표시용 HTML. 태그·따옴표·앰퍼샌드를 이스케이프하고 **줄바꿈은 글자 그대로 둔다** —
 * 그리는 쪽이 `white-space: pre-wrap` 이라 `\n` 이 줄을 바꾼다(`SafeHtml` 의 DOMPurify 허용 태그에
 * `br` 이 없어 `<br>` 로 바꾸면 사라진다). 주입은 `SafeHtml` 한 문으로만 한다.
 */
export function memoTextToHtml(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\r\n/g, '\n');
}

/**
 * 포스트 표의 **메모 셀** — 본문이 한 줄로 바로 보이고, 누르면 팝오버에서 쓰고 지운다.
 *
 * 상태는 `PostMemoProvider` 가 쥔다(워크스페이스 단위 · 팀 공유). provider 가 없으면 「-」다.
 * 저장 직후에는 구독이 돌아오기 전에도 방금 쓴 본문을 먼저 보인다(낙관) — 구독 값이 오면 그것으로 바뀐다.
 * 체험 플랜(`planBlocked`)이면 메모 팝오버 대신 플랜 업그레이드 팝오버를 연다 — 문구는 다른 플랜 게이트와 같다.
 *
 * @param {object} props
 * @param {object} props.post 표의 행 — 키는 `getPostMemoKey` 규칙
 * @param {number} [props.lines] 보이는 줄 수(기본 3)
 */
export default function PostMemoCell({ post, lines = MEMO_CELL_LINES }: { post: Record<string, unknown>; lines?: number }) {
  const ctx = usePostMemoOptional();
  const { wrap } = useDemoActionGuard();
  const planUpgrade = usePlanUpgradePopover();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  /** 저장 직후의 본문 — 구독 값이 바뀌면 버린다 */
  const [optimistic, setOptimistic] = useState<string | null>(null);

  const memoDoc = ctx?.getMemo(post) ?? null;
  const subscribedText = String(memoDoc?.memo ?? '');
  useEffect(() => { setOptimistic(null); }, [subscribedText]);
  const memoText = optimistic ?? subscribedText;
  const hasMemo = memoText.length > 0;

  const planBlocked = Boolean(ctx?.planBlocked);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (planBlocked) {
        planUpgrade.handleOpen({ currentTarget: e.currentTarget });
        return;
      }
      setDraft(memoText);
      setAnchorEl(e.currentTarget);
    },
    [memoText, planBlocked, planUpgrade]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const persist = useCallback(
    async (next: string) => {
      if (!ctx || saving) return;
      setSaving(true);
      try {
        await ctx.setMemo(post, next);
        setOptimistic(next.trim());
        toast.success(next.trim() ? '메모가 저장되었습니다.' : '메모가 삭제되었습니다.');
        setAnchorEl(null);
      } catch (err) {
        toast.error((err as { message?: string })?.message ?? '메모 저장에 실패했습니다.');
      } finally {
        setSaving(false);
      }
    },
    [ctx, post, saving]
  );

  const handleSave = wrap(() => persist(draft), DEMO_MEMO_MSG);
  const handleDelete = wrap(() => persist(''), DEMO_MEMO_MSG);

  if (!ctx) {
    return <Typography sx={{ fontSize: '13.5px', color: 'grey.400' }}>-</Typography>;
  }

  const open = Boolean(anchorEl);

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, width: MEMO_CELL_WIDTH }}>
      <Tooltip title={planBlocked ? PLAN_BLOCKED_TOOLTIP : hasMemo ? '' : '메모 남기기'} arrow disableHoverListener={hasMemo && !planBlocked}>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={planBlocked ? PLAN_BLOCKED_TOOLTIP : hasMemo ? '메모 보기' : '메모 남기기'}
          sx={{ color: hasMemo ? 'primary.main' : 'grey.400', flexShrink: 0 }}
        >
          <Iconify icon="solar:notes-linear" width={18} />
        </IconButton>
      </Tooltip>
      {hasMemo && (
        <Tooltip
          title={<SafeHtml as="div" html={memoTextToHtml(memoText)} style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} />}
          arrow
        >
          <Box
            onClick={handleOpen}
            sx={{
              fontSize: '12px',
              lineHeight: 1.4,
              color: 'text.primary',
              cursor: 'pointer',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: lines,
              WebkitBoxOrient: 'vertical',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              textAlign: 'left',
              minWidth: 0,
            }}
          >
            <SafeHtml html={memoTextToHtml(memoText)} />
          </Box>
        </Tooltip>
      )}

      {/* 체험 플랜에서만 마운트 — 팝오버가 라우터를 물기 때문에 표의 모든 줄에 실리면 안 된다 */}
      {planBlocked && (
        <PlanUpgradePopover
          anchorEl={planUpgrade.anchorEl}
          open={planUpgrade.open}
          onClose={planUpgrade.handleClose}
          type={{ status: 'upgrade', isEvent: true, isForceClosed: true }}
          description={POST_MEMO_UPGRADE_DESCRIPTION}
        />
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1.5, width: 320 }, onClick: (e: React.MouseEvent) => e.stopPropagation() } }}
      >
        <Stack spacing={1}>
          <Typography sx={{ fontSize: '13.5px', fontWeight: 'Bold' }}>메모</Typography>
          <TextField
            autoFocus
            multiline
            minRows={3}
            maxRows={8}
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, POST_MEMO_MAX_LENGTH))}
            placeholder="이 포스트에 대한 팀 메모"
            slotProps={{ htmlInput: { maxLength: POST_MEMO_MAX_LENGTH } }}
            sx={{ '& .MuiInputBase-input': { fontSize: '13.5px' } }}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: '11px', color: 'grey.500' }}>
              {draft.length} / {POST_MEMO_MAX_LENGTH}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {hasMemo && (
                <Button size="small" color="error" onClick={handleDelete} disabled={saving}>
                  삭제
                </Button>
              )}
              <Button size="small" onClick={handleClose} disabled={saving}>
                취소
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={saving || !ctx.canUse || draft.trim() === memoText.trim()}
              >
                저장
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
}
